import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { ProductCard } from '../components/ProductCard';

export function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categoryPages, setCategoryPages] = useState([]);
  const [subProducts, setSubProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    async function fetchProducts() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/produkter?limit=100');
        if (res.ok) {
          const data = await res.json();
          const items = (data.items || []).filter(
            (item) => item.url_slug !== 'produkter' && item.url_slug !== 'produkter-kelfae-lofter-aps'
          );

          const cats = items.filter((item) => item.is_category_page);
          const prods = items.filter((item) => !item.is_category_page);

          setProducts(items);
          setCategoryPages(cats);
          setSubProducts(prods);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  const categories = ['Alle', ...categoryPages.map((c) => c.title)];

  const filteredSubProducts = subProducts.filter((p) => {
    const matchesCategory = selectedCategory === 'Alle' || p.category === selectedCategory;
    const matchesSearch =
      searchTerm === '' ||
      p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.summary && p.summary.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const filteredCategories = categoryPages.filter((c) => {
    if (selectedCategory !== 'Alle' && c.title !== selectedCategory) return false;
    if (searchTerm !== '') {
      return (
        c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.summary && c.summary.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    return true;
  });

  return (
    <>
      <SEO
        title="Produkter | KELFAE LOFTER ApS"
        description="Gå på opdagelse i vores store program af akustiklofter, mineraluldsplader, flådelofter, AMF Thermatex, bafler og skinnesystemer til professionelle kunder."
      />

      <section className="bg-neutral-100 border-b border-neutral-200 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-neutral-900 bg-[#7FDCA7]/30 px-3 py-1 rounded-full border border-[#7FDCA7]/50 inline-block">
              Vores Produkter
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-neutral-900 font-serif-heading tracking-tight">
              Professionelle loft- og akustikløsninger
            </h1>
            <p className="text-sm sm:text-base text-neutral-600 leading-relaxed">
              Hos Kelfae Lofter ApS har vi en mangeårig knowhow i branchen. Vi har et stort program i akustiklofter fra førende europæiske producenter til kontorlokaler, undervisningslokaler, storkøkkener og andre store åbne rum.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {loading ? (
            <div className="py-20 text-center text-neutral-500 text-sm">
              <i className="fa-solid fa-spinner fa-spin mr-2 text-[#7FDCA7]" aria-hidden="true" />
              Henter samtlige produkter og kategorier fra CMS...
            </div>
          ) : (
            <div className="space-y-16">
              {/* Category Grid Section */}
              {filteredCategories.length > 0 && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between border-b border-neutral-200 pb-4">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 font-serif-heading">
                        {selectedCategory === 'Alle' ? 'Produktkategorier' : selectedCategory}
                      </h2>
                      <p className="text-xs text-neutral-500 mt-1">
                        Klik på en kategori for at se detaljerede specifikationer og produkter.
                      </p>
                    </div>
                  </div>

                  {/* Category Cards Grid - 2 Columns on Tablet (sm/md), 6-Column Grid Layout on Desktop (lg) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6">
                    {filteredCategories.map((cat, index) => {
                      const fallbackImage = 'https://assets.auxodev.dk/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/lofter-444.jpg';
                      const imageSrc = cat.images?.[0] || fallbackImage;
                      const isWide =
                        cat.title === 'Flådeloft' ||
                        cat.title === 'Bafler' ||
                        cat.url_slug?.includes('fladeloft') ||
                        cat.url_slug?.includes('bafler');

                      return (
                        <div
                          key={cat.id || cat.url_slug}
                          className={`group relative col-span-1 ${isWide ? 'lg:col-span-3' : 'lg:col-span-2'}`}
                        >
                          <Link
                            to={`/produkter/${cat.url_slug}`}
                            className={`group relative block w-full overflow-hidden bg-neutral-900 rounded-xs shadow-md transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${
                              isWide ? 'aspect-[16/9] sm:aspect-square lg:aspect-[16/9]' : 'aspect-square'
                            } border border-neutral-800/60 hover:border-[#7FDCA7]`}
                          >
                            {/* Background Image */}
                            <img
                              src={imageSrc}
                              alt={cat.title}
                              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                              loading="lazy"
                            />

                            {/* Multi-layer Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/90 via-neutral-950/45 to-neutral-900/25 transition-opacity duration-500 group-hover:opacity-95" />

                            {/* Top Index & Indicator */}
                            <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10 pointer-events-none">
                              <span className="text-[10px] font-mono font-bold tracking-widest text-neutral-300/80 uppercase bg-black/40 backdrop-blur-xs px-2.5 py-1 rounded-full border border-white/10 group-hover:border-[#7FDCA7]/40 group-hover:text-white transition-all">
                                0{index + 1}
                              </span>
                              <span className="w-2 h-2 rounded-full bg-[#7FDCA7]/70 group-hover:bg-[#7FDCA7] group-hover:shadow-[0_0_8px_#7FDCA7] transition-all duration-300" />
                            </div>

                            {/* Centered Content */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center z-10">
                              <h3 className="font-serif-heading font-bold text-xl sm:text-2xl lg:text-3xl text-white drop-shadow-md leading-tight group-hover:text-[#7FDCA7] transition-colors duration-300">
                                {cat.title}
                              </h3>

                              <div className="w-8 group-hover:w-16 h-0.5 bg-[#7FDCA7] my-3 rounded-full opacity-80 group-hover:opacity-100 transition-all duration-300 shadow-[0_0_6px_#7FDCA7]" />

                              <span className="text-xs font-semibold uppercase tracking-wider text-[#7FDCA7] opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 flex items-center gap-1.5 mt-1">
                                <span>Se kategori</span>
                                <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                              </span>
                            </div>

                            {/* Bottom Glowing Border */}
                            <div className="absolute bottom-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-[#7FDCA7] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {filteredCategories.length === 0 && (
                <div className="py-16 text-center bg-neutral-50 border border-neutral-200 rounded-2xl space-y-3">
                  <i className="fa-solid fa-box-open text-3xl text-neutral-400" aria-hidden="true" />
                  <p className="text-sm font-semibold text-neutral-800">Ingen kategorier fundet.</p>
                  <p className="text-xs text-neutral-500">Prøv at ændre din søgning eller vælge en anden kategori.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
